import React, { useEffect, useState } from 'react';
import { adminOverview } from '../../lib/adminApi';
import type { AdminOverview } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';

/*
 * Velaigal /admin — the admin dashboard overview.
 *
 * A read-only snapshot backed by adminOverview: five count-tile groups
 * (Users / Jobs / Applications / Screenings / Requests) plus a recent
 * activity feed sourced from audit_log (see server/src/admin/audit.ts for
 * the exact action strings this maps through ACTION_VERB). There's nothing
 * to mutate here — Users and Requests own their own actions.
 */

interface TileSpec {
  label: string;
  value: number;
}

interface GroupSpec {
  title: string;
  tiles: TileSpec[];
}

function buildGroups(overview: AdminOverview): GroupSpec[] {
  return [
    {
      title: 'Users',
      tiles: [
        { label: 'Total', value: overview.users.total },
        { label: 'Job seekers', value: overview.users.jobseekers },
        { label: 'Recruiters', value: overview.users.hr },
        { label: 'Admins', value: overview.users.admins },
        { label: 'Disabled', value: overview.users.disabled },
      ],
    },
    {
      title: 'Jobs',
      tiles: [
        { label: 'Total', value: overview.jobs.total },
        { label: 'Draft', value: overview.jobs.draft },
        { label: 'Published', value: overview.jobs.published },
        { label: 'Closed', value: overview.jobs.closed },
      ],
    },
    {
      title: 'Applications',
      tiles: [
        { label: 'Total', value: overview.applications.total },
        { label: 'Submitted', value: overview.applications.submitted },
        { label: 'Under review', value: overview.applications.under_review },
        { label: 'Shortlisted', value: overview.applications.shortlisted },
        { label: 'Rejected', value: overview.applications.rejected },
      ],
    },
    {
      title: 'Screenings',
      tiles: [
        { label: 'Queued', value: overview.screenings.queued },
        { label: 'Running', value: overview.screenings.running },
        { label: 'Done', value: overview.screenings.done },
        { label: 'Failed', value: overview.screenings.failed },
        { label: 'Manual review', value: overview.screenings.manual_review },
      ],
    },
    {
      title: 'Requests',
      tiles: [
        { label: 'Contact unhandled', value: overview.requests.contactUnhandled },
        { label: 'Demo unhandled', value: overview.requests.demoUnhandled },
      ],
    },
  ];
}

// Mirrors the AuditAction union in server/src/admin/audit.ts. An action not
// in this map (a future audit type this page hasn't been taught yet) still
// renders — just as its raw dotted string — rather than disappearing.
const ACTION_VERB: Record<string, string> = {
  'user.disable': 'disabled',
  'user.enable': 'enabled',
  'user.delete': 'deleted',
  'contact.handled': 'marked handled',
  'contact.unhandled': 'marked unhandled',
  'demo.handled': 'marked handled',
  'demo.unhandled': 'marked unhandled',
  'company.create': 'created',
  'company.update': 'updated',
  'company.member.add': 'attached a recruiter to',
  'company.member.role': 'changed a recruiter role at',
  'company.member.remove': 'removed a recruiter from',
};

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading the dashboard.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load the dashboard.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const StatTile: React.FC<TileSpec> = ({ label, value }) => (
  <div className="stat-card">
    <span className="label-editorial block mb-2">{label}</span>
    <p className="font-display text-3xl font-medium text-ink">{value.toLocaleString()}</p>
  </div>
);

const ActivityRow: React.FC<{ item: AdminOverview['recentActivity'][number] }> = ({ item }) => {
  const verb = ACTION_VERB[item.action] ?? item.action;
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-hairline last:border-0">
      <p className="text-sm text-ink min-w-0 truncate">
        <span className="font-medium">{item.actorName ?? 'Admin'}</span> {verb} {item.targetType}
      </p>
      <span className="text-xs text-ink-tertiary flex-shrink-0">{formatRelativeDate(item.createdAt)}</span>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load effect in src/pages/HR/MyJobs.tsx.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await adminOverview();
        if (cancelled) return;
        setOverview(res);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadNonce]);

  return (
    <div className="max-w-5xl mx-auto">
      <span className="label-editorial block mb-3">Admin</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Dashboard</h1>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : overview ? (
        <>
          <div className="space-y-8">
            {buildGroups(overview).map((group) => (
              <section key={group.title}>
                <span className="label-editorial block mb-3">{group.title}</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {group.tiles.map((tile) => (
                    <StatTile key={tile.label} label={tile.label} value={tile.value} />
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section className="mt-10">
            <span className="label-editorial block mb-3">Recent activity</span>
            {overview.recentActivity.length === 0 ? (
              <p className="text-sm text-ink-tertiary">No activity yet.</p>
            ) : (
              <div className="card">
                {overview.recentActivity.map((item) => (
                  <ActivityRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default Dashboard;
